import { createLocation, History, Location, LocationDescriptor } from 'history'
import { getConsoleUrl } from 'shared/config/config'

export const MANAGEMENT_PATH = '/manage'

export function isManagementPath(path: string) {
  return path === MANAGEMENT_PATH || path.startsWith(`${MANAGEMENT_PATH}/`)
}

export function managementHref<T extends string | null | undefined>(path: T): T {
  if (!path || !isManagementPath(window.location.pathname)) return path
  const absolute = path.startsWith(`${window.location.origin}/`)
  const relative = absolute ? path.slice(window.location.origin.length) : path
  if (!relative.startsWith('/') || relative.startsWith('//')) return path
  if (
    isManagementPath(relative) ||
    /^\/(?:api|ct-admin|media|static|login|signin|forgot-password|reset-password)(?:[/?#]|$)/.test(relative)
  )
    return path
  const support = window.location.pathname.match(/^\/manage\/company-support\/[^/]+(?=\/|$)/)?.[0]
  const base = support && !relative.startsWith('/company-support/') ? support : MANAGEMENT_PATH
  return `${absolute ? window.location.origin : ''}${base}${relative}` as T
}

export function managementNotificationHref(link: string) {
  if (!isManagementPath(window.location.pathname)) return link
  try {
    const url = new URL(link, window.location.origin)
    if (
      url.origin === window.location.origin ||
      url.origin === new URL(getConsoleUrl()).origin ||
      /^https:\/\/(?:fleet-)?console\.cloudtrucks(?:-staging)?\.com$/.test(url.origin)
    ) {
      const path = isManagementPath(url.pathname) ? url.pathname : `${MANAGEMENT_PATH}${url.pathname}`
      return `${window.location.origin}${path}${url.search}${url.hash}`
    }
  } catch {
    return link
  }
  return link
}

export function managementPathname() {
  const path = window.location.pathname
  return isManagementPath(path) ? path.slice(MANAGEMENT_PATH.length) || '/' : path
}

// One browser history drives the app shell and the existing administration routes.
export function scopedHistory(parent: History, base: string): History {
  const local = (location: Location) => ({
    ...location,
    pathname:
      location.pathname === base
        ? '/'
        : location.pathname.startsWith(`${base}/`)
          ? location.pathname.slice(base.length)
          : location.pathname,
  })
  const full = (to: LocationDescriptor, state?: unknown) => {
    const location = createLocation(to, state, undefined, local(parent.location))
    const authentication = /^\/(?:login|signin|forgot-password|reset-password)(?:\/|$)/.test(
      location.pathname
    )
    return {
      ...location,
      // Shared buttons may turn an already-prefixed href into a router link.
      pathname:
        authentication || isManagementPath(location.pathname)
          ? location.pathname
          : `${base}${location.pathname}`,
    }
  }
  return {
    get length() {
      return parent.length
    },
    get action() {
      return parent.action
    },
    get location() {
      return local(parent.location)
    },
    push: (to: LocationDescriptor, state?: unknown) => parent.push(full(to, state)),
    replace: (to: LocationDescriptor, state?: unknown) => parent.replace(full(to, state)),
    createHref: (to) => parent.createHref(full(to)),
    go: (n) => parent.go(n),
    goBack: () => parent.goBack(),
    goForward: () => parent.goForward(),
    listen: (listener) => parent.listen((location, action) => listener(local(location), action)),
    block: (prompt) =>
      parent.block(
        typeof prompt === 'function' ? (location, action) => prompt(local(location), action) : prompt
      ),
  }
}
